import { Shield, Lock, CheckCircle, FileCheck, Globe } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface TrustBadgesProps {
  variant?: "default" | "compact" | "inline";
  className?: string;
  showLabels?: boolean;
}

const TRUST_BADGES = [
  {
    icon: Shield,
    label: "Pagamenti sicuri",
    description: "Stripe",
    color: "text-accent-green-500",
    bgColor: "bg-accent-green-500/10",
  },
  {
    icon: Lock,
    label: "GDPR compliant",
    description: "Privacy garantita",
    color: "text-primary",
    bgColor: "bg-primary/10",
  },
  {
    icon: CheckCircle,
    label: "Supporto in italiano",
    description: "Assistenza dedicata",
    color: "text-accent-orange-500",
    bgColor: "bg-accent-orange-500/10",
  },
  {
    icon: FileCheck,
    label: "SCA & PSD2 compliant",
    description: "Pagamenti certificati",
    color: "text-blue-600",
    bgColor: "bg-blue-600/10",
  },
  {
    icon: Globe,
    label: "VAT-ready",
    description: "Fatturazione inclusa",
    color: "text-purple-600",
    bgColor: "bg-purple-600/10",
  },
];

export default function TrustBadges({ variant = "default", className, showLabels = true }: TrustBadgesProps) {
  if (variant === "inline") {
    return (
      <div className={cn("flex flex-wrap items-center gap-3", className)}>
        {TRUST_BADGES.map((badge, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <badge.icon className={cn("h-4 w-4", badge.color)} />
            <span className="text-sm font-medium text-gray-700">{badge.label}</span>
          </div>
        ))}
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div className={cn("flex flex-wrap items-center justify-center gap-2", className)}>
        {TRUST_BADGES.map((badge, idx) => (
          <Badge key={idx} variant="secondary" className="flex items-center gap-1.5 px-3 py-1.5 whitespace-nowrap">
            <badge.icon className={cn("h-3.5 w-3.5 flex-shrink-0", badge.color)} />
            {showLabels && <span className="text-xs font-medium">{badge.label}</span>}
          </Badge>
        ))}
      </div>
    );
  }

  // Default variant - full badges with descriptions
  return (
    <div className={cn("grid grid-cols-2 md:grid-cols-5 gap-4", className)}>
      {TRUST_BADGES.map((badge, idx) => (
        <div
          key={idx}
          className={cn(
            "flex flex-col items-center justify-center p-4 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow",
            badge.bgColor
          )}
        >
          <badge.icon className={cn("h-8 w-8 mb-2", badge.color)} />
          <div className="text-center">
            <div className={cn("text-sm font-semibold", badge.color)}>{badge.label}</div>
            {showLabels && (
              <div className="text-xs text-gray-600 mt-0.5">{badge.description}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
